import { Button, Card, Label } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import ListWrapper from 'components/molecules/list-wrapper/ListWrapper';
import { useScroll } from 'components/templates/application/ApplicationTemplate';
import { FormikHelpers } from 'formik';
import { useIsMobile } from 'hooks/useIsMobile';
import { useParamId } from 'hooks/useParamId';
import { useTranslation } from 'react-i18next';
import {
  PiEye,
  PiNavigationArrow,
  PiPlay,
  PiPlus,
  PiThumbsDown,
  PiThumbsUp,
  PiVideo,
} from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useGetChannelVideosInfinite, usePostChannelVideoById } from 'services/channel';
import { IAddVideoById } from 'services/channel/types/query';
import { formatDateAndHour } from 'utils/global/date.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { object, string } from 'yup';

const ChannelVideo = () => {
  const channelId = useParamId('channel');

  const getVideos = useGetChannelVideosInfinite(channelId);

  const addVideo = usePostChannelVideoById(channelId);
  const scroll = useScroll({ fetchNextPage: getVideos.fetchNextPage });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const navigateToYtVideo = (videoId: string) => {
    const targetPath = `https://www.youtube.com/watch?v=${videoId}`;
    window.open(targetPath, '_blank');
  };

  const onAddVideo = async (
    values: IAddVideoById,
    formikHelpers: FormikHelpers<IAddVideoById>,
    actions: PopupActions | null,
  ) =>
    await addVideo.mutateAsync(values, {
      onSuccess: () => {
        getVideos.refetch();
        actions?.close();
      },
      onError: (error) => onErrorShowAllErrorsToast(error),
    });

  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col w-full items-end gap-4">
      <div className="flex mr-4">
        <MagellanPopups.Form
          header={
            <>
              <PiVideo /> {t('channel.popup.input.addByVideoId')}
            </>
          }
          validationSchema={object().shape({
            videoId: string().required(t('formik-error.required')),
          })}
          initialValues={{ videoId: '' }}
          onSubmit={onAddVideo}
          trigger={
            <Button type="submit" onlyIcon>
              <Button.Icon icon={<PiPlus />} />
            </Button>
          }
        >
          <FormInput.Text
            type="text"
            label={t('channel.popup.input.byId')}
            placeholder="PUwfdhRc5jYswE"
            appBackground
            name={'videoId'}
          />
        </MagellanPopups.Form>
      </div>
      <ListWrapper
        items={getVideos.data?.pages.flat()}
        isFetchingNextPage={getVideos.isFetchingNextPage}
        isLoading={getVideos.isLoading}
        item={(video) => (
          <Card
            key={video.id}
            image={video.videoImageLink}
            header={video.videoTitle}
            description={`${
              video.dateOfPublication ? formatDateAndHour(video.dateOfPublication) : ''
            } (${video.externalVideoId})`}
            status={
              !isMobile ? (
                <div className="flex gap-2 items-center">
                  {video.isShorts && (
                    <Label color="red" size="compact" withText>
                      <PiPlay size={12} />
                      SHORTS
                    </Label>
                  )}
                  <Label title="Views" size="compact" withText style={{ minWidth: 120 }}>
                    <PiEye /> {video.views.toLocaleString()}
                  </Label>
                  <Label title="Likes" size="compact" withText style={{ minWidth: 100 }}>
                    <PiThumbsUp /> {video.likesCount.toLocaleString()}
                  </Label>
                  {!!video.dislikesCount && (
                    <Label size="compact" withText style={{ minWidth: 100 }}>
                      <PiThumbsDown /> {video.dislikesCount.toLocaleString()}
                    </Label>
                  )}
                  <Button
                    type="button"
                    isClear
                    className="flip-icon-x"
                    onlyIcon
                    onClick={() => navigateToYtVideo(video.externalVideoId)}
                  >
                    <Button.Icon icon={<PiNavigationArrow />} />
                  </Button>
                </div>
              ) : undefined
            }
            onClick={() => navigate(`${video.id}/data`)}
            adjustImageWidth
          />
        )}
      />
    </div>
  );
};

export default ChannelVideo;
